import { useOrganizationManager } from "@/composables/organization-manager";
import { types, useServices } from "@/lib/openapi";
import {
    AuthStatus,
    useAuthenticationManager,
} from "@/stores/authentication-store";
import { defineStore, storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";
import { ref, watch } from "vue";

export type Stek = types.Stek;

export const useSteksStore = defineStore("steks", () => {
    const toast = useToast();
    const isLoading = ref(false);
    const steks = ref<Stek[]>();
    const { organization } = useOrganizationManager();
    const { authStatus } = useAuthenticationManager();
    const api = useServices({
        requires: {
            auth: true,
            organization: true,
            stek: false,
        },
    });

    const controller = ref(new AbortController());

    const getSteks = async () => {
        if (
            authStatus.value !== AuthStatus.SIGNED_IN ||
            !organization.value?.identity
        ) {
            return;
        }

        controller.value.abort();
        controller.value = new AbortController();

        isLoading.value = true;
        try {
            const { response, data } =
                await api.startStekStekMgtGetSteksByOrganizationStartPost({
                    body: undefined,
                    signal: controller.value.signal,
                });

            if (response.ok) {
                steks.value = data?.sort((a, b) =>
                    a.name.localeCompare(b.name)
                );
            } else {
                if (response.status === 0) {
                    // Request was aborted by the AbortController
                    return;
                }
                throw new Error(
                    `Request failed with status ${response.status}`
                );
            }
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                return;
            } else {
                throw e;
            }
        } finally {
            isLoading.value = false;
        }
    };

    // Cannot get steks if we aren't signed in.
    watch(
        authStatus,
        (newAuthStatus) => {
            if (newAuthStatus === AuthStatus.SIGNED_IN) {
                getSteks();
            }
        },
        { immediate: true }
    );

    watch(
        () => organization.value?.identity,
        async (identity) => {
            if (identity) {
                await getSteks();
            } else {
                steks.value = undefined;
            }
        }
    );

    /**
     * Create a new Stek.
     */
    const create = async (
        name: string,
        fqdn?: string,
        logo?: File,
        opts: { showToast: boolean } = { showToast: true }
    ) => {
        isLoading.value = true;
        const { response } = await api.startStekStekMgtCreateStekStartPost({
            body: { name, fqdn, logo },
        });

        if (response.ok) {
            await getSteks();
        } else if (opts.showToast) {
            toast.add({
                severity: "error",
                summary: "Could not create stek",
                detail: "Server returned " + response.status,
            });
        }
        isLoading.value = false;
    };

    // /**
    //  * Update an existing Stek's FQDN and/or logo.
    //  */
    // const update = async (
    //     updates: {
    //         fqdn?: string;
    //         logo?: File;
    //     },
    //     opts: { showToast: boolean } = { showToast: true }
    // ) => {
    //     isLoading.value = true;
    //     try {
    //         const { response } = await api.startStekStekMgtUpdateStekStartPost({
    //             body: {
    //                 ...updates,
    //             },
    //         });

    //         if (response.ok) {
    //             await getSteks();
    //             if (opts.showToast) {
    //                 toast.add({
    //                     severity: "success",
    //                     summary: "Stek updated successfully",
    //                 });
    //             }
    //         } else if (opts.showToast) {
    //             toast.add({
    //                 severity: "error",
    //                 summary: "Could not update stek",
    //                 detail: "Server returned " + response.status,
    //             });
    //         }
    //     } finally {
    //         isLoading.value = false;
    //     }
    // };

    return {
        create,
        getSteks,
        steks,
        isLoading,
    };
});

export const useSteksManager = () => storeToRefs(useSteksStore());
